import { gql } from 'apollo-boost';

export const allShipments = gql`
  query shipment($limit: Int!, $offset: Int!) {
    shipment {
      paged(limit: $limit, offset: $offset) {
        results {
          id
          shipmentStatusId
          changedUtc
          changedByUser {
            firstName
            lastName
          }
          stops {
            id
            actualTimeLocal
            plannedFromTimeLocal
            plannedToTimeLocal
            plannedFromTimeUtc
            plannedToTimeUtc
            projectedFromTimeLocal
            projectedToTimeLocal
            projectedFromTimeUtc
            projectedToTimeUtc
            contactId
            deliveryNotes
            locationId
            locationName
            openFromTimeLocal
            openToTimeLocal
            pickupNotes
            shipmentStopStatusId
            stopNumber
            timeZoneId
            locationAddress {
              address1
              address2
              city
              state
              postalCode
              country
              lat
              long
            }
            contactInfo {
              email
              firstName
              lastName
              phone
            }
          }
        }
        limit
        offset
        totalCount
        hasMoreData
      }
    }
  }
`;

// pared down version of shipment query for index where we just pull in
// the subset of data we need for the grid
export const allShipmentsIndex = gql`
  query shipment($orgId: ID!, $limit: Int!, $offset: Int!, $query: String!) {
    shipment {
      pagedQuery(orgId: $orgId, limit: $limit, offset: $offset, query: $query) {
        results {
          id
          actualDelivery
          actualPickup
          carrierName
          changedUtc
          deliveryDate
          direction
          destinationCity
          destinationPostalCode
          destinationState
          destinationLatitude
          destinationLongitude
          originCity
          originPostalCode
          originState
          originLatitude
          originLongitude
          pickupDate
          plannedDelivery
          plannedPickup
          projectedDelivery
          projectedPickup
          status
        }
        totalCount
        hasMoreData
      }
    }
  }
`;

export const shipmentByBusinessId = gql`
  {
    shipment {
      byBusinessId {
        rowVersion
        id
      }
    }
  }
`;

export const shipmentByDivisionId = gql`
  {
    shipment {
      byDivisionId {
        rowVersion
        id
      }
    }
  }
`;

export const shipmentByStatus = gql`
  query shipment($shipmentStatusId: ShipmentStatus!, $orgId: ID!) {
    shipment {
      byStatus(shipmentStatusId: $shipmentStatusId, orgId: $orgId)
    }
  }
`;

export const shipmentByInTransitStatus = gql`
  query shipment(
    $shipmentInTransitStatusId: ShipmentInTransitStatus!
    $orgId: ID!
  ) {
    shipment {
      byInTransitStatus(
        shipmentInTransitStatusId: $shipmentInTransitStatusId
        orgId: $orgId
      )
    }
  }
`;

export const shipmentCount = gql`
  query shipment($orgId: ID!) {
    shipment {
      count(orgId: $orgId)
    }
  }
`;

export const shipmentById = gql`
  query shipment($id: ID!) {
    shipment {
      byId(id: $id) {
        id
        requestedServices
        requestedEquipment
        rowVersion
        orgId
        glCodeId
        businessId
        references {
          stringId
          values
        }
        comments {
          content
          id
          createdByUser {
            firstName
            lastName
          }
          createdByUserId
          changedUtc
          wasEdited
        }
        divisionId
        proNumber
        previousShipmentStatusId
        totalLinearFeet
        bolNumber
        bolInstructions
        shipmentModeId
        shipmentStatusId
        shipmentDirectionId
        bolInstructions
        carrierPickupNumber
        shipperPickupNumber
        trackingNotificationEmailAddresses {
          id
          emailAddress
        }
        selectedShipmentRate {
          id
          customRateItemGuid
          rateListGuid
          rateItemGuid
          carrierFuelTotal
          deliveryDateTimeLocal
          transitDays
          carrierGrandTotal
          carrierAccessorialTotal
          carrierLineHaulTotal
          serviceLevelId
          carrierQuoteNumber
          isVolumeRate
          isCustomRate
          lcpExceptionReasonId
          lcpExceptionReasonNotes
          notes
          carrierName
          carrier {
            id
            name
            orgId
            dispatchPhoneNumber
            logoUrl
            supportsAutoTender
          }
        }
        shipmentRates {
          id
          customRateItemGuid
          isCustomRate
          laneTypeId
          rateListGuid
          rateItemGuid
          carrierFuelTotal
          deliveryDateTimeLocal
          transitDays
          carrierGrandTotal
          carrierAccessorialTotal
          carrierLineHaulTotal
          serviceLevelId
          canBeSelected
          carrierQuoteNumber
          isVolumeRate
          isCustomRate
          notes
          carrierName
          carrier {
            id
            name
            orgId
          }
          messages {
            message
            severity
          }
        }
        billToInfo {
          billTo
          companyName
          billToAddress {
            address1
            address2
            postalCode
            city
            state
            country
            lat
            long
          }
          billToContactInfo {
            firstName
            lastName
            email
            phone
          }
        }
        changedUtc
        changedByUser {
          firstName
          lastName
          loginEmail
        }
        createdUtc
        createdByUser {
          firstName
          lastName
          loginEmail
        }
        externalId
        stops {
          id
          actualTimeLocal
          plannedToTimeLocal
          plannedFromTimeLocal
          projectedToTimeLocal
          contactId
          deliveryNotes
          locationId
          locationName
          openFromTimeLocal
          openToTimeLocal
          pickupNotes
          shipmentStopStatusId
          stopNumber
          timeZoneId
          carrierWasLate
          locationAddress {
            address1
            address2
            city
            state
            postalCode
            country
            lat
            long
          }
          contactInfo {
            email
            firstName
            lastName
            phone
          }
        }
        handlingUnits {
          id
          currency
          value
          doNotStack
          deliveryStopId
          deliveryStop {
            stopNumber
          }
          densityUnit
          density
          handlingUnitNumber
          packageTypeId
          pickupStopId
          unitCount
          volumeUnit
          volume
          tareWeightInfo {
            weightUnit
            weight
          }
          actualWeightInfo {
            weight
            weightUnit
          }
          dimensionInfo {
            dimensionUnit
            height
            width
            length
          }
          deliveryStop {
            stopNumber
          }
          freightInfo {
            freightClassId
            nmfcItemCode
            nmfcSubCode
          }
          pickupStop {
            stopNumber
          }
          shipmentItems {
            id
            quantity
            product {
              sku
              name
              description
              packageTypeId
              pieceCount
              isHazMat
              volume
              volumeUnit
              density
              densityUnit
              valueInfo {
                value
                currency
              }
              weightInfo {
                weight
                weightUnit
              }
              freightInfo {
                freightClassId
                nmfcSubCode
                nmfcItemCode
              }
              dimensionInfo {
                length
                width
                height
                dimensionUnit
              }
              hazMat {
                properShippingName
                unNumber
                comments
                contactFirstName
                contactLastName
                contactEmail
                contactPhone
                hazMatClassId
                hazMatPackingGroupId
              }
            }
          }
        }
        shipmentFiles {
          orgId
          id
          fileUploadId
          fileTypeId
          uploadedFileName
          createdUtc
          shipmentId
          shipmentDocumentTypeId
          originalFileDownloadUrlSas
          previewImageDownloadUrlSas
        }
        references {
          stringId
          values
        }
      }
    }
  }
`;

const unauthenticatedShipmentReturn = `
id
changed
requestedServices
requestedEquipment
references {
  stringId
  values
  label
}
proNumber
bolNumber
bolInstructions
shipmentStatusId
selectedShipmentRate {
  carrierName
  isCustomRate
  carrier {
    name
    logoUrl
    customerServicePhoneNumber
  }
}
billToInfo {
  companyName
  billingTerms
  billingTermsName
  billTo
  billToName
  billToAddress {
    address1
    address2
    postalCode
    city
    state
    country
    lat
    long
  }
  billToContactInfo {
    firstName
    lastName
    email
    phone
  }
}
stops {
  actualTimeLocal
  plannedToTimeLocal
  plannedFromTimeLocal
  projectedToTimeLocal
  deliveryNotes
  locationName
  openFromTimeLocal
  openToTimeLocal
  pickupNotes
  shipmentStopStatusId
  stopNumber
  carrierWasLate
  locationAddress {
    address1
    address2
    city
    state
    postalCode
    country
    lat
    long
  }
  contactInfo {
    email
    firstName
    lastName
    phone
  }
}
handlingUnits {
  currency
  value
  doNotStack
  densityUnit
  density
  packageTypeId
  unitCount
  volumeUnit
  volume
  tareWeightInfo {
    weightUnit
    weight
    weightUnitName
  }
  actualWeightInfo {
    weight
    weightUnit
    weightUnitName
  }
  dimensionInfo {
    dimensionUnit
    height
    width
    length
    dimensionUnitName
  }
  freightInfo {
    freightClassId
    nmfcItemCode
    nmfcSubCode
    freightClassName
  }
  shipmentItems {
    quantity
    product {
      sku
      name
      description
      packageTypeId
      pieceCount
      isHazMat
      volume
      volumeUnit
      density
      densityUnit
      valueInfo {
        value
        currency
        currencyName
      }
      weightInfo {
        weight
        weightUnit
        weightUnitName
      }
      freightInfo {
        freightClassId
        nmfcSubCode
        nmfcItemCode
        freightClassName
      }
      dimensionInfo {
        length
        width
        height
        dimensionUnit
        dimensionUnitName
      }
      hazMat {
        properShippingName
        unNumber
        comments
        contactFirstName
        contactLastName
        contactEmail
        contactPhone
        hazMatClassId
        hazMatPackingGroupId
      }
    }
  }
}
trackingHistory {
  statusAsOfTimeLocal
  statusDiscoveredByProviderUtc
  stopNumber
  stopType
  trackingStatusId
  trackingStatusReasonId
  trackingStatusReasonText
}
`;

export const shipmentByIdForBol = gql`
query shipmentForBol($id: ID!) {
  shipment {
    shipmentForBol(id: $id) {
      ${unauthenticatedShipmentReturn}
    }
  }
}
`;

export const unauthenticatedShipmentById = gql`
  query unauthenticatedShipment($id: ID!) {
    unauthenticated {
      shipment(id: $id) {
        ${unauthenticatedShipmentReturn}
      }
    }
  }
`;

//serviceLevelDescription add it when fixed the error
export const rateShipment = gql`
  query shipment($rateRequest: ShipmentInput!) {
    shipment {
      rateShipment(rateRequest: $rateRequest) {
        id
        customRateItemGuid
        shipmentId
        shipmentRowVersion
        canBeSelected
        rateListGuid
        rateItemGuid
        carrierFuelTotal
        deliveryDateTimeLocal
        transitDays
        carrierGrandTotal
        carrierAccessorialTotal
        carrierLineHaulTotal
        serviceLevelId
        serviceLevelDescription
        carrierQuoteNumber
        isVolumeRate
        isCustomRate
        latestDispatchTimeUtc
        carrierName
        laneTypeId
        messages {
          message
          severity
        }
        notes
        carrier {
          id
          name
          orgId
          leadTimeWindowMinutes
          dispatchPhoneNumber
          supportsAutoTender
        }
      }
    }
  }
`;

export const shipmentFormDropdowns = gql`
  query shipmentFormDropdowns($orgId: ID!) {
    contact {
      all(orgId: $orgId) {
        id
        email
        firstName
        lastName
        phone
      }
    }
    location {
      all(orgId: $orgId) {
        id
        name
        openFromTimeLocal
        openToTimeLocal
        primaryContactId
        pickupNotes
        deliveryNotes
        address {
          address1
          address2
          city
          country
          isMappable
          lat
          long
          postalCode
          state
        }
      }
    }
  }
`;

export const unassignedFiles = gql`
  query unassignedFiled($orgId: ID!, $offset: Int!, $limit: Int!) {
    shipment {
      unassignedFiles(orgId: $orgId, offset: $offset, limit: $limit) {
        results {
          orgId
          id
          fileUploadId
          fileTypeId
          uploadedFileName
          createdUtc
          shipmentId
          shipmentDocumentTypeId
          originalFileDownloadUrlSas
          previewImageDownloadUrlSas
        }
        totalCount
        hasMoreData
      }
    }
  }
`;

export const unassignedFilesCount = gql`
  query unassignedFiled($orgId: ID!, $offset: Int!, $limit: Int!) {
    shipment {
      unassignedFiles(orgId: $orgId, offset: $offset, limit: $limit) {
        totalCount
      }
    }
  }
`;

export const filesByShipment = gql`
  query filesByShipment($orgId: ID!, $shipmentId: ID!) {
    shipment {
      filesByShipment(orgId: $orgId, shipmentId: $shipmentId) {
        orgId
        id
        fileUploadId
        fileTypeId
        uploadedFileName
        createdUtc
        shipmentId
        shipmentDocumentTypeId
        originalFileDownloadUrlSas
        previewImageDownloadUrlSas
      }
    }
  }
`;

export const shipmentActivityLog = gql`
  query shipment($id: ID!) {
    shipment {
      activityLog(id: $id) {
        activityLogDetails {
          actualUser
          title
          subTitle
          createdUtc
        }
        count
      }
    }
  }
`;
export const shipmentCommentSubscribers = gql`
  query shipment($shipmentId: ID!) {
    shipment {
      commentSubscribersByShipment(shipmentId: $shipmentId) {
        id
      }
    }
  }
`;

//
export const commentsByShipmentId = gql`
  query shipment($id: ID!) {
    shipment {
      byId(id: $id) {
        comments {
          content
          id
          createdByUser {
            firstName
            lastName
          }
          changedUtc
          wasEdited
          createdByUserId
        }
      }
    }
  }
`;

export const shipmentIds = gql`
  query shipment($orgId: ID!) {
    shipment {
      all(orgId: $orgId) {
        id
      }
    }
  }
`;

export const pickedUpShipmentsGroupedByDateRangeType = gql`
  query pickedUpShipments(
    $orgId: Int!
    $dateRangeType: DateRangeType!
    $startDate: Date!
    $endDate: Date!
  ) {
    shipment {
      pickedUpShipmentsGroupedByDateRangeType(
        orgId: $orgId
        dateRangeType: $dateRangeType
        startDate: $startDate
        endDate: $endDate
      ) {
        dateRangeType
        data {
          Key
          Value
        }
      }
    }
  }
`;

export const carrierSpend = gql`
  query carrierSpend($orgId: ID!, $dateFrom: Date, $dateTo: Date) {
    carrier {
      carrierSpend(orgId: $orgId, dateFrom: $dateFrom, dateTo: $dateTo) {
        carrierName
        scac
        totalShipments
        totalSpend
      }
    }
  }
`;
